import { useMemo } from 'react';

import { EMPTY_ARRAY } from '@e-socna/empty';

import type { ThirdPartiesCmp_SomeThirdParty } from '../queries/some-third-parties.query';

import { useFindAllEnabledExternalThirdPartiesQuery } from './useFindAllEnabledExternalThirdPartiesQuery.hook';
import { useFindAllDisabledExternalThirdPartiesQuery } from './useFindAllDisabledExternalThirdPartiesQuery.hook';
import { useFindAllDeletedExternalThirdPartiesQuery } from './useFindAllDeletedExternalThirdPartiesQuery.hook';

interface UnionResult {
  readonly data:
    | Readonly<{
        findSomeThirdParties: readonly ThirdPartiesCmp_SomeThirdParty[];
      }>
    | undefined;
  readonly isLoading: boolean;
  readonly isSuccess: boolean;
}

export const useFindAllExternalThirdPartiesQuery = (
  enabled = true,
): UnionResult => {
  const deletedQuery = useFindAllDeletedExternalThirdPartiesQuery(enabled);
  const disabledQuery = useFindAllDisabledExternalThirdPartiesQuery(enabled);
  const enabledQuery = useFindAllEnabledExternalThirdPartiesQuery(enabled);

  const memoizedResult = useMemo(() => {
    const deletedData = deletedQuery.data?.findSomeThirdParties;
    const disabledData = disabledQuery.data?.findSomeThirdParties;
    const enabledData = enabledQuery.data?.findSomeThirdParties;

    const data =
      deletedData || disabledData || enabledData
        ? {
            findSomeThirdParties: [
              ...(deletedData ?? EMPTY_ARRAY),
              ...(disabledData ?? EMPTY_ARRAY),
              ...(enabledData ?? EMPTY_ARRAY),
            ],
          }
        : undefined;

    const isLoading =
      deletedQuery.isLoading ||
      disabledQuery.isLoading ||
      enabledQuery.isLoading;
    const isSuccess =
      deletedQuery.isSuccess &&
      disabledQuery.isSuccess &&
      enabledQuery.isSuccess;

    return { data, isLoading, isSuccess };
  }, [
    deletedQuery.data?.findSomeThirdParties,
    deletedQuery.isLoading,
    deletedQuery.isSuccess,
    disabledQuery.data?.findSomeThirdParties,
    disabledQuery.isLoading,
    disabledQuery.isSuccess,
    enabledQuery.data?.findSomeThirdParties,
    enabledQuery.isLoading,
    enabledQuery.isSuccess,
  ]);

  return memoizedResult;
};
